<template>
    <div
        v-if="isVisible"
        ref="contextMenu"
        class="fixed z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[160px] context-menu"
        :style="menuStyle"
        @click.stop
    >
        <button
            @click="handleEdit"
            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
            {{ $t('folders.context_menu.edit') }}
        </button>

        <button
            @click="handleCopyLink"
            class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
            </svg>
            {{ $t('folders.context_menu.copy_link') }}
        </button>


        <button
            @click="handleDelete"
            class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
        >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
            {{ $t('folders.context_menu.delete') }}
        </button>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Folder } from '@/types/GalleryType';

export default defineComponent({
    name: 'FolderContextMenuComponent',

    props: {
        folder: {
            type: Object as () => Folder,
            required: true
        },
        isVisible: {
            type: Boolean,
            default: false
        },
        position: {
            type: Object as () => { x: number; y: number },
            default: () => ({ x: 0, y: 0 })
        }
    },

    emits: ['close', 'edit', 'delete', 'copy-link'],

    data() {
        return {
            contextMenu: null as HTMLElement | null
        };
    },

    computed: {
        menuStyle(): { left: string; top: string } {
            return {
                left: `${this.position.x}px`,
                top: `${this.position.y}px`,
            };
        }
    },

    methods: {
        handleEdit() {
            this.$emit('edit', this.folder);
            this.$emit('close');
        },

        handleDelete() {
            this.$emit('delete', this.folder);
            this.$emit('close');
        },

        handleCopyLink() {
            this.$emit('copy-link', this.folder);
            this.$emit('close');
        },

        handleDocumentClick(event: MouseEvent) {
            if (this.contextMenu && !this.contextMenu.contains(event.target as Node)) {
                this.$emit('close');
            }
        },

        handleMouseDown(event: MouseEvent) {
            if (this.contextMenu && !this.contextMenu.contains(event.target as Node)) {
                this.$emit('close');
            }
        },

        handleEscape(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                this.$emit('close');
            }
        }
    },

    mounted() {
        this.contextMenu = this.$refs.contextMenu as HTMLElement;
        // Використовуємо setTimeout для того, щоб не закрити меню одразу після відкриття
        setTimeout(() => {
            document.addEventListener('click', this.handleDocumentClick, true);
            document.addEventListener('mousedown', this.handleMouseDown, true);
        }, 0);
        document.addEventListener('keydown', this.handleEscape);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick, true);
        document.removeEventListener('mousedown', this.handleMouseDown, true);
        document.removeEventListener('keydown', this.handleEscape);
    }
});
</script>

<style scoped>
/* Додаткові стилі для контекстного меню */
.fixed {
    position: fixed;
    z-index: 50;
}

/* Переконаємося, що меню завжди видиме */
.context-menu {
    pointer-events: auto;
}
</style>
